export interface ExchangeRateItem {
  id: string;
  /** 표시 라벨: USD, JPY, CNY */
  code: string;
  label: string;
  /** 1단위당 KRW (JPY는 100엔 기준 권장 시 rate*100) */
  krw: number;
  /** 단위 안내 */
  unit: string;
}

export interface ExchangeRatesSnapshot {
  rates: ExchangeRateItem[];
  baseNote: string;
  updatedAt: string;
}

interface ErApiResponse {
  result?: string;
  time_last_update_utc?: string;
  rates?: Record<string, number>;
}

/**
 * 미·일·중 → 원화 환율
 * open.er-api.com (API 키 불필요)
 * 1 USD / 100 JPY / 1 CNY 기준 KRW
 */
export async function fetchMainExchangeRates(): Promise<ExchangeRatesSnapshot> {
  const res = await fetch("https://open.er-api.com/v6/latest/USD");
  if (!res.ok) {
    throw new Error(`FX_HTTP_${res.status}`);
  }

  const data = (await res.json()) as ErApiResponse;
  if (data.result !== "success" || !data.rates) {
    throw new Error("FX_EMPTY");
  }

  const { rates } = data;
  const krwPerUsd = rates.KRW;
  const jpyPerUsd = rates.JPY;
  const cnyPerUsd = rates.CNY;

  if (!krwPerUsd || !jpyPerUsd || !cnyPerUsd) {
    throw new Error("FX_MISSING_CURRENCY");
  }

  // 1 USD = KRW
  const usdKrw = krwPerUsd;
  // 100 JPY = KRW
  const jpy100Krw = (krwPerUsd / jpyPerUsd) * 100;
  // 1 CNY = KRW
  const cnyKrw = krwPerUsd / cnyPerUsd;

  return {
    baseNote: "1 USD / 100 JPY / 1 CNY → KRW",
    updatedAt: data.time_last_update_utc ?? new Date().toISOString(),
    rates: [
      {
        id: "usd",
        code: "USD",
        label: "미국 달러",
        krw: usdKrw,
        unit: "1 USD",
      },
      {
        id: "jpy",
        code: "JPY",
        label: "일본 엔",
        krw: jpy100Krw,
        unit: "100 JPY",
      },
      {
        id: "cny",
        code: "CNY",
        label: "중국 위안",
        krw: cnyKrw,
        unit: "1 CNY",
      },
    ],
  };
}

export function formatKrw(value: number): string {
  return (
    value.toLocaleString("ko-KR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " 원"
  );
}
